import { useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Cache key for session storage
const SESSION_CACHE_KEY = 'auth_session_cache';

export function useAuth() {
  const [user, setUser] = useState<User | null>(() => {
    // Try to restore user from cache for instant initial state
    try {
      const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.user || null;
      }
    } catch {
      // Ignore cache errors
    }
    return null;
  });
  
  const [session, setSession] = useState<Session | null>(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.session || null;
      }
    } catch {
      // Ignore cache errors
    }
    return null;
  });
  
  const [loading, setLoading] = useState(() => {
    // If we have cached session, don't show loading
    try {
      const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
      return !cached;
    } catch {
      return true;
    }
  });

  // Update cache when session changes
  const updateCache = useCallback((session: Session | null) => {
    try {
      if (session) {
        sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({
          session,
          user: session.user,
          timestamp: Date.now()
        }));
      } else {
        sessionStorage.removeItem(SESSION_CACHE_KEY);
      }
    } catch {
      // Ignore cache errors
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener - this is the single source of truth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        updateCache(newSession);
      }
    );

    // Only fetch session if we don't have a cached one
    // The onAuthStateChange will update with the real session
    if (!session) {
      supabase.auth.getSession().then(({ data: { session: fetchedSession } }) => {
        setSession(fetchedSession);
        setUser(fetchedSession?.user ?? null);
        setLoading(false);
        updateCache(fetchedSession);
      });
    }

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, nome: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { nome }
      }
    });

    if (!error && data.user) {
      // Create profile after signup
      await supabase.from('profiles').insert({
        user_id: data.user.id,
        nome,
        email
      });
    }

    return { error };
  }, []);

  const signOut = useCallback(async () => {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    
    return { error };
  }, []);

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    isAuthenticated: !!session,
  };
}
